/**
 * Panel Container Component
 *
 * The expanded panel wrapper with animations and responsive behavior.
 * Uses Radix Dialog for accessibility and focus management.
 * Supports swipe gestures on mobile and drag-to-resize on desktop.
 * Can be positioned at the bottom or right side of the screen.
 */

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useEffect, useState, useRef, useCallback } from 'react';

import { cn } from '@pray/shared/utils/styles';

import type { PanelContainerProps } from '../types';

import styles from './PanelContainer.module.scss';

// Swipe threshold in pixels
const SWIPE_THRESHOLD = 50;

// Resize constraints
const MIN_SIZE = 250;
const DEFAULT_SIZE = 400;
const NAVBAR_HEIGHT = 64;
const SIDEBAR_WIDTH = 264;

export function PanelContainer({ isExpanded, position, onClose, children, className }: PanelContainerProps) {
  const [isMobile, setIsMobile] = useState(false);
  // Track size (height for bottom, width for right)
  const [size, setSize] = useState<number>(DEFAULT_SIZE);
  const [isDragging, setIsDragging] = useState(false);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const resizeHandleRef = useRef<HTMLDivElement>(null);
  const touchStartRef = useRef<{ y: number; time: number } | null>(null);
  const dragStartRef = useRef<{ pos: number; size: number } | null>(null);

  const isRight = position === 'right';

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Touch start handler for swipe gesture
  const handleTouchStart = useCallback((e: TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = {
      y: touch.clientY,
      time: Date.now(),
    };
  }, []);

  // Touch end handler for swipe gesture
  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!touchStartRef.current) return;

      const touch = e.changedTouches[0];
      const deltaY = touch.clientY - touchStartRef.current.y;
      const deltaTime = Date.now() - touchStartRef.current.time;

      // Check if swipe down within timeout (300ms)
      if (deltaY > SWIPE_THRESHOLD && deltaTime < 300) {
        onClose();
      }

      touchStartRef.current = null;
    },
    [onClose]
  );

  // Attach swipe listeners to drag handle
  useEffect(() => {
    const dragHandle = dragHandleRef.current;
    if (!dragHandle || !isMobile) return undefined;

    dragHandle.addEventListener('touchstart', handleTouchStart, { passive: true });
    dragHandle.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      dragHandle.removeEventListener('touchstart', handleTouchStart);
      dragHandle.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMobile, handleTouchStart, handleTouchEnd]);

  // Mouse drag resize handlers for desktop
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isMobile) return;
      e.preventDefault();
      setIsDragging(true);
      dragStartRef.current = {
        pos: isRight ? e.clientX : e.clientY,
        size,
      };
    },
    [isMobile, size, isRight]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !dragStartRef.current) return;

      if (isRight) {
        // For right position: drag left edge to resize width
        const maxWidth = window.innerWidth - SIDEBAR_WIDTH;
        const delta = dragStartRef.current.pos - e.clientX;
        const newWidth = Math.min(maxWidth, Math.max(MIN_SIZE, dragStartRef.current.size + delta));
        setSize(newWidth);
      } else {
        // For bottom position: drag top edge to resize height
        const maxHeight = window.innerHeight - NAVBAR_HEIGHT;
        const delta = dragStartRef.current.pos - e.clientY;
        const newHeight = Math.min(maxHeight, Math.max(MIN_SIZE, dragStartRef.current.size + delta));
        setSize(newHeight);
      }
    },
    [isDragging, isRight]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    dragStartRef.current = null;
  }, []);

  // Attach mouse drag listeners to document
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = isRight ? 'ew-resize' : 'ns-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp, isRight]);

  // Handle resize via keyboard
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const step = 50;
    const maxSize = isRight ? window.innerWidth - SIDEBAR_WIDTH : window.innerHeight - NAVBAR_HEIGHT;

    if (e.key === 'ArrowUp' && !isRight) {
      e.preventDefault();
      setSize((s) => Math.min(maxSize, s + step));
    } else if (e.key === 'ArrowDown' && !isRight) {
      e.preventDefault();
      setSize((s) => Math.max(MIN_SIZE, s - step));
    } else if (e.key === 'ArrowLeft' && isRight) {
      e.preventDefault();
      setSize((s) => Math.min(maxSize, s + step));
    } else if (e.key === 'ArrowRight' && isRight) {
      e.preventDefault();
      setSize((s) => Math.max(MIN_SIZE, s - step));
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <DialogPrimitive.Root open={isExpanded} onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        {/* Mobile overlay */}
        {isMobile && <DialogPrimitive.Overlay className={styles.overlay} />}

        <DialogPrimitive.Content
          className={cn(
            styles.container,
            isMobile && styles.mobile,
            isDragging && styles.dragging,
            isRight && styles.right,
            className
          )}
          style={
            {
              // eslint-disable-next-line no-nested-ternary
              '--panel-size': isMobile ? (isRight ? '100vw' : '80vh') : `${size}px`,
            } as React.CSSProperties
          }
          onKeyDown={handleKeyDown}
          aria-describedby={undefined}
        >
          {/* Drag handle for mobile - supports swipe down to close */}
          {isMobile && !isRight && (
            <div
              ref={dragHandleRef}
              className={styles.dragHandle}
              role="button"
              tabIndex={0}
              aria-label="Swipe down to close"
              onClick={onClose}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClose();
                }
              }}
            >
              <div className={styles.dragBar} />
            </div>
          )}

          {/* Resize handle for desktop - drag to resize */}
          {!isMobile && (
            <div
              ref={resizeHandleRef}
              className={cn(styles.resizeHandle, isRight && styles.resizeHandleRight)}
              role="slider"
              tabIndex={0}
              aria-label="Resize panel"
              aria-valuemin={MIN_SIZE}
              aria-valuemax={isRight ? window.innerWidth - SIDEBAR_WIDTH : window.innerHeight - NAVBAR_HEIGHT}
              aria-valuenow={size}
              onMouseDown={handleMouseDown}
              onKeyDown={(e) => {
                const step = 50;
                const maxSize = isRight ? window.innerWidth - SIDEBAR_WIDTH : window.innerHeight - NAVBAR_HEIGHT;
                if ((e.key === 'ArrowUp' && !isRight) || (e.key === 'ArrowLeft' && isRight)) {
                  e.preventDefault();
                  setSize((s) => Math.min(maxSize, s + step));
                } else if ((e.key === 'ArrowDown' && !isRight) || (e.key === 'ArrowRight' && isRight)) {
                  e.preventDefault();
                  setSize((s) => Math.max(MIN_SIZE, s - step));
                }
              }}
              title="Drag to resize"
            />
          )}

          {/* Panel content */}
          <div className={styles.content}>{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export default PanelContainer;
